import { ApiClient } from "@ucl/library";
import { JointAccountOwner } from "@/types";

class JointAccountOwnerApiClient {
  private _apiClient: ApiClient;

  constructor(apiClient: ApiClient) {
    this._apiClient = apiClient;
  }

  /**
   * Creates a new joint account owner for a financial application.
   */
  createJointAccountOwner = async (financialApplicationId: string) => {
    return this._apiClient.post<JointAccountOwner>(
      `api/financial-application/${financialApplicationId}/joint-account-owners/`
    );
  };

  /**
   * Validates joint account owners for a financial application.
   */
  validateJointAccountOwners = async (financialApplicationId: string) => {
    return this._apiClient.post<Record<string, Record<string, string[]>>>(
      `api/financial-application/${financialApplicationId}/joint-account-owners/validate`
    );
  };

  /**
   * Retrieves a joint account owner by their ID.
   */
  getJointAccountOwner = async (financialApplicationId: string, jointOwnerId: string) => {
    return this._apiClient.get<JointAccountOwner>(
      `api/financial-application/${financialApplicationId}/joint-account-owners/${jointOwnerId}`
    );
  };

  /**
   * Retrieves all joint account owners for a financial application.
   */
  getJointAccountOwners = async (financialApplicationId: string) => {
    return this._apiClient.get<JointAccountOwner[]>(
      `api/financial-application/${financialApplicationId}/joint-account-owners`
    );
  };

  getJointAccountOwnerById = async (jointOwnerId: string) => {
    return this._apiClient.get<JointAccountOwner>(`api/joint-account-owners/${jointOwnerId}`);
  };

  /**
   * Updates an existing joint account owner for a financial application.
   */
  updateJointAccountOwner = async (
    financialApplicationId: string,
    jointOwnerId: string,
    data: JointAccountOwner
  ) => {
    return this._apiClient.put<JointAccountOwner>(
      `api/financial-application/${financialApplicationId}/joint-account-owners/${jointOwnerId}`,
      data
    );
  };

  /**
   * Updates the identity documents for a joint account owner.
   */
  updateJointAccountOwnerIdentityDocuments = async (
    financialApplicationId: string,
    jointOwnerId: string,
    data: JointAccountOwner
  ) => {
    return this._apiClient.put<JointAccountOwner>(
      `api/financial-application/${financialApplicationId}/joint-account-owners/${jointOwnerId}/documents`,
      data
    );
  };

  /**
   * Deletes a joint account owner from a financial application.
   */
  deleteJointAccountOwner = async (financialApplicationId: string, jointOwnerId: string) => {
    return this._apiClient.delete<JointAccountOwner>(
      `api/financial-application/${financialApplicationId}/joint-account-owners/${jointOwnerId}`
    );
  };

  /**
   * Retrieves a list of joint account owner IDs for a financial application.
   */
  getJointAccountOwnerIds = async (financialApplicationId: string) => {
    return this._apiClient.get<string[]>(
      `api/financial-application/${financialApplicationId}/joint-account-owner-ids`
    );
  };
}

export default JointAccountOwnerApiClient;
